import { useEffect, useRef, useState } from "react";
import { Viewer } from "@photo-sphere-viewer/core";
import { MarkersPlugin } from "@photo-sphere-viewer/markers-plugin";
import "@photo-sphere-viewer/core/index.css";
import "@photo-sphere-viewer/markers-plugin/index.css";
import { AlertTriangle, RotateCw } from "lucide-react";
import { Button } from "../ui/button";

async function fetchBlobWithRetry(url, signal, attempts = 4) {
  // Large panorama fetches have shown intermittent mid-transfer failures in
  // this dev environment (headers resolve 200, but reading the body throws
  // "Failed to fetch") even though isolated stress tests of the same endpoint
  // succeed consistently — most likely flakiness in Docker Desktop's network
  // proxy layer rather than the app itself. A short automatic retry papers
  // over the transient case instead of surfacing it as a hard failure.
  let lastError;
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await fetch(url, { signal });
      if (!res.ok) throw new Error(`Failed to fetch panorama (status ${res.status})`);
      return await res.blob();
    } catch (e) {
      if (e.name === "AbortError") throw e;
      lastError = e;
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, 400 * (i + 1)));
    }
  }
  throw lastError;
}

function markerHtml(number, isActive) {
  return `<div style="
    display:flex;align-items:center;justify-content:center;
    width:28px;height:28px;border-radius:9999px;
    background:${isActive ? "#3D6355" : "#C24A2E"};
    color:white;font-size:12px;font-weight:600;
    border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4);
    cursor:pointer;transform:${isActive ? "scale(1.25)" : "scale(1)"};
  ">${number}</div>`;
}

// Equirectangular / partial-panorama viewer with drag-to-look-around and
// click-to-drop-pin commenting. Handles panoramas cropped to a narrower
// vertical FOV (e.g. real-estate renders exported at 16:9) by computing crop
// bounds from the actual loaded image dimensions via panoData, rather than
// assuming a strict 2:1 equirectangular image.
export default function Review360Viewer({ asset, fileUrl, comments, activeCommentId, onPinDraft, onSelectComment, goToYawPitch }) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const markersRef = useRef(null);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    setError(null);
    let cancelled = false;
    let viewer = null;
    let objectUrl = null;
    const controller = new AbortController();

    const timeoutId = setTimeout(() => {
      if (cancelled) return;
      setError("Timed out loading the panorama — the file may be too large or the connection too slow.");
    }, 25000);

    // We fetch the panorama ourselves (with our own AbortController) instead
    // of letting Photo Sphere Viewer load it directly. PSV's loader wraps the
    // fetch in a manually-pumped ReadableStream for progress tracking, and
    // aborting mid-pump — which happens routinely under React StrictMode's
    // dev-only double-mount (mount -> cleanup -> mount) — throws an uncaught
    // "enqueue on a closed stream" error that crashes the whole page. Handing
    // PSV an already-resolved blob: URL means it never does a tracked network
    // fetch itself, so that failure mode can't occur; our own fetch's abort
    // is a standard, well-behaved promise rejection we control and catch.
    (async () => {
      try {
        const blob = await fetchBlobWithRetry(fileUrl, controller.signal);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);

        viewer = new Viewer({
          container: containerRef.current,
          panorama: objectUrl,
          panoData: (image) => {
            const fullWidth = image.width;
            // Normally a full equirectangular is exactly 2:1 (fullHeight = fullWidth / 2).
            // Some panorama exports are taller than that ratio (e.g. slightly more than
            // a hemisphere) — clamp so fullHeight never ends up smaller than the actual
            // image, which would otherwise produce a negative (invalid) croppedY.
            const fullHeight = Math.max(image.height, fullWidth / 2);
            return {
              fullWidth,
              fullHeight,
              croppedWidth: image.width,
              croppedHeight: image.height,
              croppedX: 0,
              croppedY: (fullHeight - image.height) / 2,
            };
          },
          navbar: ["zoom", "fullscreen"],
          defaultZoomLvl: 0,
          plugins: [[MarkersPlugin, { markers: [] }]],
        });
        viewerRef.current = viewer;
        markersRef.current = viewer.getPlugin(MarkersPlugin);

        viewer.addEventListener("click", ({ data }) => {
          if (data.rightclick) return;
          onPinDraft?.({ yaw: data.yaw, pitch: data.pitch });
        });
        markersRef.current.addEventListener("select-marker", ({ marker }) => {
          onSelectComment?.(marker.id);
        });
        viewer.addEventListener("ready", () => {
          clearTimeout(timeoutId);
        });
        viewer.addEventListener("panorama-error", (e) => {
          clearTimeout(timeoutId);
          if (cancelled) return;
          console.error("Review360Viewer panorama-error:", e);
          setError(e?.error?.message || e?.message || "Failed to load the panorama image.");
        });
      } catch (e) {
        clearTimeout(timeoutId);
        if (cancelled || e.name === "AbortError") return;
        console.error("Review360Viewer fetch error:", e);
        setError(e.message || "Failed to load the panorama image.");
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timeoutId);
      viewer?.destroy();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      viewerRef.current = null;
      markersRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset?.id, fileUrl, retryKey]);

  useEffect(() => {
    if (!markersRef.current) return;
    const pins = comments.filter((c) => c.pin_yaw != null && c.pin_pitch != null && !c.parent_comment_id);
    markersRef.current.setMarkers(
      pins.map((c, i) => ({
        id: c.id,
        position: { yaw: c.pin_yaw, pitch: c.pin_pitch },
        html: markerHtml(i + 1, c.id === activeCommentId),
        anchor: "center center",
        tooltip: c.body,
      }))
    );
  }, [comments, activeCommentId]);

  useEffect(() => {
    if (goToYawPitch && viewerRef.current) {
      viewerRef.current.animate({ yaw: goToYawPitch.yaw, pitch: goToYawPitch.pitch, speed: "600ms" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goToYawPitch]);

  // flex-1 (not w-full) because the parent is a `justify-content: center` flex
  // row — percentage widths don't reliably resolve there, but flex-grow does.
  // Photo Sphere Viewer measures the container on mount; if it's 0px wide it
  // just sits on its loading spinner forever instead of erroring.
  return (
    <div className="relative flex-1 rounded-lg border border-border overflow-hidden" style={{ height: "65vh", minWidth: 0 }}>
      <div ref={containerRef} className="w-full h-full" />
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/95 text-center px-6">
          <AlertTriangle className="w-6 h-6 text-amber-600" />
          <p className="text-sm text-muted-foreground max-w-sm">{error}</p>
          <Button size="sm" variant="outline" onClick={() => setRetryKey((k) => k + 1)}>
            <RotateCw className="w-3.5 h-3.5 mr-1.5" />
            Retry
          </Button>
        </div>
      )}
    </div>
  );
}
